import React from 'react';

export function AdvancedCustomizationStep({ formData, onChange }) {
  const complianceOptions = [
    { value: 'pci-focus', label: 'PCI DSS Compliance Focus', description: 'Emphasize security and compliance benefits' },
    { value: 'gdpr-focus', label: 'GDPR & Data Privacy', description: 'Highlight data protection capabilities' },
    { value: 'regional-compliance', label: 'Regional Compliance', description: 'Address local regulatory requirements' },
    { value: 'no-compliance', label: 'No Compliance Focus', description: 'General messaging without specific compliance emphasis' }
  ];

  const competitivePositioning = [
    { value: 'direct-comparison', label: 'Direct Comparison', description: 'Compare TNS directly with named competitors' },
    { value: 'category-leadership', label: 'Category Leadership', description: 'Position TNS as the industry leader' },
    { value: 'alternative-approach', label: 'Alternative Approach', description: 'Present TNS as a better way without naming competitors' },
    { value: 'no-competitive', label: 'No Competitive Focus', description: 'Focus solely on TNS benefits and capabilities' }
  ];

  const technicalDepthLevels = [
    { value: 'high-level', label: 'High-Level Overview', description: 'Business benefits and strategic value' },
    { value: 'moderate-detail', label: 'Moderate Technical Detail', description: 'Some technical concepts with business context' },
    { value: 'deep-technical', label: 'Deep Technical Detail', description: 'Comprehensive technical implementation details' }
  ];

  const callToActionTypes = [
    { value: 'demo-request', label: 'Request Demo', description: 'Schedule a product demonstration' },
    { value: 'consultation', label: 'Book Consultation', description: 'Free consultation with TNS experts' },
    { value: 'partner-inquiry', label: 'Partner Inquiry', description: 'Explore partnership opportunities' },
    { value: 'download-resource', label: 'Download Resource', description: 'Access additional materials or tools' },
    { value: 'contact-sales', label: 'Contact Sales', description: 'Connect with TNS sales team' },
    { value: 'learn-more', label: 'Learn More', description: 'Visit TNS website for additional information' }
  ];

  const brandVoiceOptions = [
    { value: 'tns-voice', label: 'TNS Standard', description: 'Professional expert that removes complexity. Authoritative yet approachable, solution-focused, partnership-oriented. Enterprise B2B with confidence-building clarity.' },
    { value: 'authoritative', label: 'Authoritative', description: 'Confident, expert, industry-leading' },
    { value: 'consultative', label: 'Consultative', description: 'Advisory, helpful, problem-solving focused' },
    { value: 'innovative', label: 'Innovative', description: 'Forward-thinking, technology-focused, cutting-edge' },
    { value: 'collaborative', label: 'Collaborative', description: 'Partnership-oriented, relationship-building' },
    { value: 'professional', label: 'Professional', description: 'Business-focused, straightforward, reliable' },
    { value: 'custom', label: 'Custom Brand Voice', description: 'Define your own specific brand voice and tone' }
  ];

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Advanced Customization</h2>
        <p className="text-gray-600 mb-8">
          Fine-tune your content approach with advanced options. These settings help ensure your content aligns with your specific marketing strategy and brand positioning.
        </p>
      </div>

      {/* Compliance Messaging */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Compliance Messaging
        </label>
        <p className="text-sm text-gray-500 mb-4">How prominently should compliance and regulatory topics be featured?</p>
        <div className="space-y-3">
          {complianceOptions.map((option) => (
            <div
              key={option.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${formData.complianceMessaging === option.value
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
                }`}
              onClick={() => onChange('complianceMessaging', option.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="complianceMessaging"
                  value={option.value}
                  checked={formData.complianceMessaging === option.value}
                  onChange={() => onChange('complianceMessaging', option.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {option.label}
                  </label>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Competitive Positioning */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Competitive Positioning
        </label>
        <p className="text-sm text-gray-500 mb-4">How should TNS be positioned relative to competitors?</p>
        <div className="space-y-3">
          {competitivePositioning.map((option) => (
            <div
              key={option.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${formData.competitivePositioning === option.value
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
                }`}
              onClick={() => onChange('competitivePositioning', option.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="competitivePositioning"
                  value={option.value}
                  checked={formData.competitivePositioning === option.value}
                  onChange={() => onChange('competitivePositioning', option.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {option.label}
                  </label>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Technical Depth */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Technical Depth
        </label>
        <p className="text-sm text-gray-500 mb-4">What level of technical detail should be included?</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {technicalDepthLevels.map((level) => (
            <div
              key={level.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${formData.technicalDepth === level.value
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
                }`}
              onClick={() => onChange('technicalDepth', level.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="technicalDepth"
                  value={level.value}
                  checked={formData.technicalDepth === level.value}
                  onChange={() => onChange('technicalDepth', level.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {level.label}
                  </label>
                  <p className="text-sm text-gray-500">{level.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Call to Action */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Primary Call-to-Action
        </label>
        <p className="text-sm text-gray-500 mb-4">What action should readers be encouraged to take?</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {callToActionTypes.map((cta) => (
            <div
              key={cta.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${formData.callToAction === cta.value
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
                }`}
              onClick={() => onChange('callToAction', cta.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="callToAction"
                  value={cta.value}
                  checked={formData.callToAction === cta.value}
                  onChange={() => onChange('callToAction', cta.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {cta.label}
                  </label>
                  <p className="text-sm text-gray-500">{cta.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Brand Voice */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Brand Voice
        </label>
        <p className="text-sm text-gray-500 mb-4">What tone and style should the content adopt?</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {brandVoiceOptions.map((voice) => (
            <div
              key={voice.value}
              className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${formData.brandVoice === voice.value
                ? 'border-green-500 bg-green-50'
                : 'border-gray-200 hover:border-gray-300'
                }`}
              onClick={() => onChange('brandVoice', voice.value)}
            >
              <div className="flex items-start">
                <input
                  type="radio"
                  name="brandVoice"
                  value={voice.value}
                  checked={formData.brandVoice === voice.value}
                  onChange={() => onChange('brandVoice', voice.value)}
                  className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                />
                <div className="ml-3">
                  <label className="block text-sm font-medium text-gray-900">
                    {voice.label}
                  </label>
                  <p className="text-sm text-gray-500">{voice.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Custom Brand Voice Description */}
      {formData.brandVoice === 'custom' && (
        <div>
          <label htmlFor="customBrandVoice" className="block text-sm font-medium text-gray-700 mb-4">
            Custom Brand Voice Description
          </label>
          <p className="text-sm text-gray-500 mb-4">
            Describe the specific tone, style, and personality you want the content to reflect.
          </p>
          <textarea
            id="customBrandVoice"
            name="customBrandVoice"
            rows={4}
            value={formData.customBrandVoice || ''}
            onChange={(e) => onChange('customBrandVoice', e.target.value)}
            className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
            placeholder="e.g., Friendly and approachable while maintaining technical credibility. Use conversational tone with industry expertise. Emphasize partnership and collaboration..."
          />
        </div>
      )}

      {/* Additional Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-4">
          Additional Notes (Optional)
        </label>
        <p className="text-sm text-gray-500 mb-4">
          Any specific requirements, messaging points, or guidelines for the content generation.
        </p>
        <textarea
          id="notes"
          name="notes"
          rows={4}
          value={formData.notes}
          onChange={(e) => onChange('notes', e.target.value)}
          className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-green-500 focus:border-green-500"
          placeholder="Enter any specific requirements or guidelines..."
        />
      </div>
    </div>
  );
}